import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OptionValue } from 'ncoded-component-library/build/components/molecules/Select/Select.component';
import {
  HEADING_TRANSLATIONS,
  PortfolioSellerTableKeys,
  SORTING_VALUES,
  SortingValues,
} from '../components/SellerDealTableCells/data';
import { SORTING_ORDER, SortingDirectionValues } from 'models/Sort';

const MULTI_SORT_COPY = {
  [SORTING_VALUES.numberOfFiles]: 'numberOfFiles',
  [SORTING_VALUES.numberOfLeads]: 'numberOfLeads',
  [SORTING_VALUES.commission]: 'commission',
};

export const MULTI_SORT_DIRECTION_COPY = {
  [SORTING_VALUES.stage]: {
    [SORTING_ORDER.A_TO_Z]: 'initialFirst',
    [SORTING_ORDER.Z_TO_A]: 'advancedFirst',
  },
  [SORTING_VALUES.listingStage]: {
    [SORTING_ORDER.A_TO_Z]: 'initialFirst',
    [SORTING_ORDER.Z_TO_A]: 'advancedFirst',
  },
  [SORTING_VALUES.lastUpdated]: {
    [SORTING_ORDER.A_TO_Z]: 'earliestFirst',
    [SORTING_ORDER.Z_TO_A]: 'latestFirst',
  },
  [SORTING_VALUES.price]: {
    [SORTING_ORDER.A_TO_Z]: 'ascending',
    [SORTING_ORDER.Z_TO_A]: 'descending',
  },
  [SORTING_VALUES.commission]: {
    [SORTING_ORDER.A_TO_Z]: 'ascending',
    [SORTING_ORDER.Z_TO_A]: 'descending',
  },
  [SORTING_VALUES.numberOfLeads]: {
    [SORTING_ORDER.A_TO_Z]: 'ascending',
    [SORTING_ORDER.Z_TO_A]: 'descending',
  },
  [SORTING_VALUES.numberOfFiles]: {
    [SORTING_ORDER.A_TO_Z]: 'ascending',
    [SORTING_ORDER.Z_TO_A]: 'descending',
  },
};

const useSellingMultiSort = (
  tableColumns: Partial<PortfolioSellerTableKeys>,
) => {
  const { t } = useTranslation();

  const sortColumns = useMemo(
    () => tableColumns.filter((column) => !!SORTING_VALUES[column]),
    [tableColumns],
  );

  const multiSortValues = useMemo(
    () =>
      sortColumns.map((column) => {
        const value = SORTING_VALUES[column];
        const label = t(MULTI_SORT_COPY[value] || HEADING_TRANSLATIONS[column]);

        return {
          value,
          label,
        };
      }) as OptionValue<SortingValues>[],
    [sortColumns, t],
  );

  const multiSortDirectionTranslation = useMemo(() => {
    const result: SortingDirectionValues = {};

    for (const column of sortColumns) {
      const sort = SORTING_VALUES[column];
      const value = MULTI_SORT_DIRECTION_COPY[sort];

      result[sort] = {
        [SORTING_ORDER.A_TO_Z]: t(value?.[SORTING_ORDER.A_TO_Z] || 'aToZ'),
        [SORTING_ORDER.Z_TO_A]: t(value?.[SORTING_ORDER.Z_TO_A] || 'zToA'),
      };
    }

    return result;
  }, [sortColumns, t]);

  return { multiSortValues, multiSortDirectionTranslation };
};

export default useSellingMultiSort;
